import React, { useState, useEffect, useCallback } from 'react';
import TicketStageTabs from '../components/TicketStageTabs';
import { stageOf, atStage } from '../lib/ticketStage';
import ContractorKanban from '../components/ContractorKanban';
import './ContractorDashboard.css';
import ContractorBilling from '../components/ContractorBilling';
import MyContract from '../components/contracts/MyContract';
import ContractorWorkOrders from '../components/ContractorWorkOrders';
import ContractorBilled from '../components/ContractorBilled';
import InvoicingView from '../components/InvoicingView';
import TicketListModal from '../components/TicketListModal';
import WorkOrdersList from './WorkOrdersList';
import { storedViewAsContractorId, storedViewAs, getViewAs } from '../lib/viewAs';
import axios from 'axios';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { requestWord } from '../lib/requestState';
import { fmtDate } from '../lib/when';
import AssetsPage from './AssetsPage';
import DocsPage from '../components/docs/DocsPage';
import AssetDetail from './AssetDetail';
import TicketTemplates from '../components/TicketTemplates';
import CreateTicket from './CreateTicket';
import RequestCatalogue from './catalogue/RequestCatalogue';

// Module scope: inside the component it would remount on every render.
// Classes, not inline sizes: the phone layout shrinks these (Gordon: "shrink
// filters & info boxes") and a style attribute is out of a media query's reach.
function ContractorStat({ value, label, tone, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="stat-tile is-clickable cd-stat"
            title={`Show ${label.toLowerCase()}`}>
      <div className="cd-stat-value" style={{ color: tone }}>
        {value}
      </div>
      <div className="cd-stat-label">
        {label}
      </div>
    </button>
  );
}

/**
 * `tab` is which strip tab to open, decided by the route: /my-tickets and
 * /my-work-orders are the portal with that tab lit, so a link to "my tickets"
 * and a bookmark of the old work-orders page both land here rather than on a
 * page of their own with the same content. No tab - /contractor-dashboard,
 * the bar's Portal - is the Board.
 *
 * `assetsForContractors` offers the Assets tab. AppShell decides it from
 * GET /assets/access - the register switched on, and this contractor given
 * 'read' or 'write' - so the tab appears only for a contractor who will see
 * something behind it. It is the one tab that is a door rather than a pane:
 * it opens the register at /assets, the same page staff use, which signs its
 * own calls with the portal token.
 */
function ContractorDashboard({ tab, assetsForContractors = false, docsForContractors = false }) {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  
  const [contractor, setContractor] = useState(null);
  const [assignments, setAssignments] = useState([]);
  // The requests they raised themselves (migration 064), kept apart from the
  // jobs: see fetchAssignments.
  const [raised, setRaised] = useState([]);
  const [loading, setLoading] = useState(true);
  // The stage being shown. Was a status string compared with ===, which is how
  // two of the seven statuses came to have no tab at all; it is a stage key
  // from lib/ticketStage now, and those are a partition.
  const [stage, setStage] = useState('all');
  // How the list within a stage is ordered and narrowed. Remembered per
  // browser, because this portal is used on a phone between jobs and a reload
  // that forgets "priority first" is a reload that has to be set up again.
  const remembered = (key, fallback) => {
    try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
  };
  const [sortBy, setSortBy] = useState(() => remembered('cd_sort', 'newest'));
  const [priorityFilter, setPriorityFilter] = useState(() => remembered('cd_priority', 'all'));
  const [query, setQuery] = useState('');
  useEffect(() => {
    try {
      localStorage.setItem('cd_sort', sortBy);
      localStorage.setItem('cd_priority', priorityFilter);
    } catch { /* private window: nothing to remember into */ }
  }, [sortBy, priorityFilter]);
  const [drill, setDrill] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  /*
   * THE BOARD IS WHERE THE PORTAL OPENS. Gordon, 22 October: "when i click on
   * contractor portal it should show me the board." Without a tab from the
   * route - /contractor-dashboard, which is what the bar's Portal links to -
   * this used to open on My Tickets, and because the router keeps one
   * instance of this page across /my-tickets and /contractor-dashboard, a
   * press on Portal from the tickets tab did not even reset it: the strip
   * stayed wherever it was. So no tab means Board, and a change of route
   * always sets the strip, including back to Board when the tab goes away.
   */
  const [portalTab, setPortalTab] = useState(tab || 'board');

  /*
   * TICKET TEMPLATES, FOR A CONTRACTOR NCMB HAS ALLOWED (088). Gordon, 26
   * October: "contractors can access ticket templates in their portal if
   * they have permissions to edit it". GET /templates/access answers for the
   * contractor's own token, or - for an administrator looking through View
   * as - for the contractor named. The tab is drawn only on a yes, for the
   * reason Assets waits for its answer: a tab onto "not for you" is a broken
   * link. The server checks every template request regardless.
   */
  const [templatesOpen, setTemplatesOpen] = useState(false);
  useEffect(() => {
    let live = true;
    const token = localStorage.getItem('contractor_token');
    const asId = token ? null : storedViewAsContractorId();
    if (!token && !asId) { setTemplatesOpen(false); return undefined; }
    axios.get(`${API_URL}/templates/access`, {
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      ...(asId ? { params: { contractor_id: asId } } : {})
    })
      .then((r) => { if (live) setTemplatesOpen(r.data?.as === 'contractor' && r.data?.can_manage === true); })
      .catch(() => { if (live) setTemplatesOpen(false); });
    return () => { live = false; };
  }, [API_URL]);
  // Issued Bills: the per-ticket list, or the same work grouped by invoice.
  const [billedBy, setBilledBy] = useState('ticket');
  // The route changed under us - /my-tickets to /my-work-orders, say, or to
  // /contractor-dashboard - so the strip follows it. A press on a tab still
  // only sets state: the strip is not a router, and the old addresses keep
  // working either way. The location KEY is a dependency too: pressing
  // Portal while already on /contractor-dashboard with the strip on another
  // tab changes nothing about the route but is still a navigation, and it
  // has to bring the Board back.
  const { key: arrival } = useLocation();
  useEffect(() => { setPortalTab(tab || 'board'); }, [tab, arrival]);
  // /assets/:id - one device, drawn in the Assets pane below rather than on
  // a page of its own.
  const { id: assetId } = useParams();
  // On a phone the strip scrolls sideways, and Assets - the last tab - opened
  // lit but out of sight. Bring the lit tab into the strip's view, sideways
  // only: the page itself must not jump.
  //
  // AFTER LAYOUT, AND AGAIN WHENEVER THE TABS CHANGE SIZE. On arrival at
  // /assets the strip is drawn before the portal has loaded, and a moment
  // later a count lands on one of the tabs and the strip grows by a few
  // dozen pixels - which pushed Assets back off the edge after it had been
  // brought into view. So: wait for the strip, centre the lit tab, and watch
  // the tabs; any change in their size centres it again.
  useEffect(() => {
    let frame = 0;
    let handle = 0;
    let watcher = null;
    const centre = () => {
      const lit = document.querySelector('.cd-tabs .tab-button.active');
      const strip = lit?.closest('.tabs');
      if (!lit || !strip || strip.scrollWidth <= strip.clientWidth) return;
      const s = strip.getBoundingClientRect();
      const b = lit.getBoundingClientRect();
      if (b.left < s.left || b.right > s.right) {
        strip.scrollLeft += (b.left - s.left) - (s.width - b.width) / 2;
      }
    };
    const start = () => {
      const strip = document.querySelector('.cd-tabs .tabs');
      if (!strip || strip.clientWidth === 0) {
        if (frame++ < 30) handle = requestAnimationFrame(start);
        return;
      }
      centre();
      if (typeof ResizeObserver === 'function') {
        watcher = new ResizeObserver(() => centre());
        strip.querySelectorAll('.tab-button').forEach((t) => watcher.observe(t));
        watcher.observe(strip);
      }
    };
    handle = requestAnimationFrame(start);
    return () => { cancelAnimationFrame(handle); if (watcher) watcher.disconnect(); };
  }, [portalTab, assetId]);
  // Whose portal this is. Read from the resolved contractor rather than straight
  // from localStorage: an admin using "View as" has no contractor_id stored, so
  // the Availability and Billing tabs were being handed null and could not load
  // anything.
  const contractorId = contractor?.id || localStorage.getItem('contractor_id');
  const contractorToken = localStorage.getItem('contractor_token');

  const fetchAssignments = useCallback(async (contractorId) => {
    try {
      setLoading(true);
      setLoadError(null);
      // An admin using "View as" has no contractor token. Sending an empty
      // bearer would fail the header check outright, so send the header only
      // when there is actually a token to send.
      const contractorToken = localStorage.getItem('contractor_token');
      const response = await axios.get(`${API_URL}/contractors/${contractorId}/assignments`,
        contractorToken
          ? { headers: { Authorization: `Bearer ${contractorToken}` } }
          : undefined);
      const data = response.data;
      const rows = Array.isArray(data) ? data : (data?.assignments ?? []);
      /*
       * TWO KINDS OF ROW SINCE MIGRATION 064. The same list now carries the
       * tickets a contractor RAISED - a request to the firm for a licence, a
       * part, an account - beside the jobs assigned to them, each row saying
       * which (assigned_to_me / raised_by_me). A request is not work to do,
       * so it stays off the board, out of the stage counts and out of the
       * seven tiles, and is listed as its own group under My Tickets. A row
       * from an older server carries neither flag and is a job, as every row
       * was; a request the firm then assigned back to them is both, and is a
       * job.
       */
      setAssignments(rows.filter((r) => r.assigned_to_me !== false));
      setRaised(rows.filter((r) => r.raised_by_me === true && r.assigned_to_me === false));
    } catch (error) {
      console.error('Error fetching assignments:', error);
      // A failed fetch used to leave the list empty and silent, which reads
      // exactly like having no work assigned. Say which it was.
      const status = error.response?.status;
      if (status === 401 && localStorage.getItem('contractor_token')) {
        navigate('/contractor-login');
        return;
      }
      setAssignments([]);
      setRaised([]);
      setLoadError(
        status === 401 || status === 403
          ? 'Not permitted to read this contractor\u2019s tickets.'
          : status
            ? `Could not load tickets (server returned ${status}).`
            : 'Could not reach the server to load tickets.'
      );
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    // An admin using "View as" opens this portal without a contractor token.
    // Read ungated: the gated version only answers once /auth/me has confirmed
    // the administrator, which is after this effect runs - so it returned
    // nothing, the page fell through to "no token", and redirected to the
    // contractor login. That is what made View as ask for the contractor's own
    // credentials. The API still refuses the data to anyone who is not an
    // administrator, so nothing is granted by reading it early.
    const impersonated = storedViewAsContractorId();
    if (impersonated) {
      const v = getViewAs();
      setContractor({ name: v?.name || 'Contractor', id: impersonated });
      fetchAssignments(impersonated);
      return;
    }

    const contractorName = localStorage.getItem('contractor_name');
    const contractorId = localStorage.getItem('contractor_id');
    const token = localStorage.getItem('contractor_token');

    if (!token) {
      // A staff impersonation has no business here, and sending it to the
      // contractor login asks an administrator for somebody else's password -
      // which is what happened. Go back to the app instead.
      if (storedViewAs()?.kind === 'user') {
        navigate('/my-tickets');
        return;
      }
      navigate('/contractor-login');
      return;
    }

    setContractor({ name: contractorName, id: contractorId });
    fetchAssignments(contractorId);
  }, [navigate, fetchAssignments]);

  /*
   * Signing out moved into the navbar's account menu, with everybody else's,
   * when this page stopped drawing a header of its own. The three keys it
   * forgot are now forgotten by contractorSignOut() in lib/contractorAuth -
   * one place, because a stale contractor_token is not harmless: it is what
   * made an administrator's browser poll for incoming calls as the contractor.
   */

  /**
   * Move a ticket along.
   *
   * This dropdown has been on the page all along and has never once worked.
   * It sent { status: 'In Progress' } to PUT /api/tickets/:id, which is gated
   * on requireStaff - a contractor holds a portal token, not a staff identity,
   * so the server answered 401 - and which only ever accepted status_id, so
   * even an administrator got "No editable fields supplied" back. Nothing
   * read the reply, so the list updated locally and the ticket did not move.
   *
   * PATCH /tickets/:id/status takes the name, checks the caller is the
   * contractor the ticket names, and records the change in the ticket's
   * history. A failure is now shown rather than swallowed into an alert.
   */
  const handleStatusUpdate = async (ticketId, newStatus) => {
    if (!newStatus) return;
    try {
      setUpdatingStatus(ticketId);
      setStatusError(null);
      const token = localStorage.getItem('contractor_token');
      const res = await axios.patch(
        `${API_URL}/tickets/${ticketId}/status`,
        { status: newStatus },
        // An admin using "View as" has no contractor token; sending
        // "Bearer null" fails the header check outright, so send nothing and
        // let the app's own interceptor attach their staff token.
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setAssignments((current) => current.map((a) =>
        a.id === ticketId
          ? { ...a, status: res.data?.status || newStatus, status_id: res.data?.status_id ?? a.status_id }
          : a));
    } catch (error) {
      setStatusError(error.response?.data?.error
        || 'Could not change that ticket\u2019s status.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  /**
   * Open one of my tickets: the whole ticket, on its own page, from any tab
   * and from the lists behind the tiles.
   *
   * This used to open a summary in a dialog - enough to change the status and
   * write a comment, but not the device, its diagnostics, the instructions,
   * or room to work. Gordon: clicking a ticket "should open the detailed
   * ticket". The contractor's own ticket page - for an administrator using
   * "View as" too. They used to be sent to the staff page with its controls,
   * which is not what the contractor sees and left the Viewing-as banner
   * over a page the contractor could never open; /my-tickets/:id draws the
   * contractor's page for both now (App.js), and the Billing tab's lines,
   * which always linked there, no longer dead-end on the list.
   */
  const ticketPath = useCallback((ticketId) => `/my-tickets/${ticketId}`, []);
  const openAssignment = useCallback((ticketId) => {
    navigate(ticketPath(ticketId));
  }, [navigate, ticketPath]);

  const byStatus = (name) => assignments.filter((a) => a.status === name);

  /*
   * The lists behind the new tiles. Derived from `assignments`, which is
   * already loaded, and grouped with lib/ticketStage so a tile and a tab can
   * never disagree about where a ticket is.
   *
   * No hours tile, deliberately. /contractors/:id/assignments returns no
   * hours column - the row carries status, priority, invoice_number and the
   * sent-back fields - so "hours this month" would have had to be invented
   * here or guessed from something that does not mean it. The sent-back pile
   * is real, is on the row, and nothing else on this page surfaces it.
   */
  const stageIn = (...keys) => assignments.filter((a) => keys.includes(stageOf(a)));

  // Open or being worked on AND urgent. The question at 8am is what to do
  // first, and neither the tabs nor the total answer it.
  const needsAttention = stageIn('open', 'in_progress')
    .filter((a) => ['High', 'Critical'].includes(a.priority));

  // Mine, and I cannot move it: somebody owes me an approval or an answer.
  const waitingOnOthers = stageIn('awaiting', 'on_hold');

  // Finished and not invoiced - the money. lib/ticketStage is explicit that
  // this is what the stage means, because invoicing is what closes a ticket.
  const unbilled = atStage(assignments, 'unbilled');

  // Handed back by an approver. sent_back_at is the decision; the reason is
  // what it is for.
  const sentBack = assignments.filter((a) => a.sent_back_at || a.sent_back_reason);

  // One rule, in lib/ticketStage, shared with the admin tickets list and with
  // the tabs above - so the tab that says 4 and the list it filters cannot
  // disagree, and no status can fall between them.
  const filteredAssignments = atStage(assignments, stage);

  /*
   * SORT AND FILTER, within the stage.
   *
   * Gordon: "show and highlight status of the tickets and in addition allow
   * sorting and filtering."
   *
   * The stage tabs stay the primary filter, because their counts are the
   * partition lib/ticketStage promises - every ticket in exactly one tab,
   * the tabs adding up to All. These three narrow WITHIN the chosen stage and
   * never change the counts on the tabs, so the row above keeps telling the
   * truth about how much work there is while the list shows the part of it
   * being looked for.
   *
   * No request is made: every field here is already on the assignment row.
   */
  const matchesFilters = (a) => {
    if (priorityFilter !== 'all' && String(a.priority || '') !== priorityFilter) return false;
    if (query) {
      const q = query.toLowerCase();
      const hay = `${a.ticket_number || ''} ${a.title || ''} ${a.category || ''} ${a.department || ''}`
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const PRIORITY_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };
  const STATUS_RANK = ['Open', 'In Progress', 'On Hold', 'Awaiting Approval',
                       'Resolved', 'Approved', 'Closed'];
  const when = (v) => (v ? new Date(v).getTime() : 0);
  const SORTS = {
    newest: (a, b) => when(b.created_at) - when(a.created_at),
    oldest: (a, b) => when(a.created_at) - when(b.created_at),
    updated: (a, b) => when(b.updated_at) - when(a.updated_at),
    priority: (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
      || when(b.created_at) - when(a.created_at),
    status: (a, b) => {
      const ra = STATUS_RANK.indexOf(a.status); const rb = STATUS_RANK.indexOf(b.status);
      return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb) || when(b.created_at) - when(a.created_at);
    }
  };

  // filter() copies, so the sort never touches the state array behind it -
  // atStage hands back `assignments` itself for All.
  const visibleAssignments = filteredAssignments
    .filter(matchesFilters)
    .sort(SORTS[sortBy] || SORTS.newest);

  const narrowed = priorityFilter !== 'all' || Boolean(query);
  const clearFilters = () => { setPriorityFilter('all'); setQuery(''); };

  const getStatusColor = (status) => {
    const colors = {
      'Open': '#dc2626',
      'In Progress': '#0891b2',
      'On Hold': '#d97706',
      'Resolved': '#059669',
      'Closed': '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': '#0891b2',
      'Medium': '#d97706',
      'High': '#dc2626',
      'Critical': '#7c2d12'
    };
    return colors[priority] || '#6b7280';
  };

  return (
    /* Classes rather than inline styles, because the phone layout needs to
       reach the padding and the header, and a style attribute cannot be put
       behind a media query. */
    <div className="cd-page">
      {/* Header. HIDDEN ON A PHONE (ContractorDashboard.css): Gordon asked for
          the space back - "remove the Welcome XX - your assignments & work
          tickets" - and on a 390px screen this block was a 2rem title, a
          subtitle, a rule and 3.5rem of spacing before the first tab. The
          account menu in the navbar already names the contractor. */}
      <div className="cd-welcome">
        <div>
          {/* The product mark used to be drawn here because the portal had
              no navbar to put it in. It has one now, carrying the Jarvis mark
              on the left and NCMB's on the right exactly as every other page
              does, so repeating it here was simply the logo twice. */}
          <h1 className="cd-welcome-title">
            Welcome, {contractor?.name}
          </h1>
          <p className="cd-welcome-sub">
            Your work assignments and tickets
          </p>
        </div>
        {/*
          * THE SECOND HEADER IS GONE.
          *
          * This corner used to carry the firm mark, a link into chat, the
          * bell, the theme switch and a Logout button - because the portal
          * hid the navbar and something had to do a navbar's job. The result
          * was two headers in one application: this one on the portal, the
          * real navbar on /chat, with different controls in a different
          * order. Gordon: "when i click on chat in the contractor view i have
          * a different header and menu. Can we standardise here?"
          *
          * So the navbar is drawn on the portal too (see App.js), and it
          * carries all five of these for a contractor exactly as it does for
          * everybody else. What is left here is the welcome line, which is
          * page content rather than chrome.
          */}
      </div>

      {/* STICKY, just under the navbar. Gordon: "the sliding menu bar (my
          tickets, board, work & time, billing) - make this sticky just below
          the Jarvis logo." The offset is --navbar-h, which AppShell measures,
          because the bar's height on a phone is two wrapped rows and not a
          number. The rule is in ContractorDashboard.css, scoped to .cd-page so
          no other page's tab strip starts sticking. */}
      <div className="tabs-container cd-tabs">
        <div className="tabs" role="tablist">
          {/* THE ORDER IS GORDON'S, 21 October: "Board · My Work Orders · My
              Tickets · Work & Time · Billing · Issued Bills · Assets". The
              board first, because it is the view of the day; the two lists
              of work next; then the three money tabs in the order money
              moves - time logged, time to invoice, invoices issued. */}
          {[
            ['board', 'Board'],
            // The work orders issued to this contractor - the page that was
            // /my-work-orders and a word of its own in the top bar. A tab
            // here now, beside the tickets those orders are for; the old
            // address still opens the portal on this tab (App.js).
            ['myorders', 'My work orders'],
            ['work', 'My tickets'],
            ['orders', 'Work & time'],
            ['billing', 'Billing'],
            // Billing is the queue of what still needs invoicing; Issued Bills
            // is what already has been. Adjacent on purpose - the question
            // "did I invoice this one" is answered by looking one tab across.
            // "Billed" read as a state a ticket might be in rather than as a
            // list of the bills themselves.
            ['billed', 'Issued bills'],
            // Only when the server says so - see the note on the props - or
            // when they are standing on it already (a device link followed
            // before the access check answered): a lit tab that is not in
            // the strip is a strip saying nothing is open.
            ...(assetsForContractors || portalTab === 'assets' ? [['assets', 'Assets']] : []),
            // Documentation (Gordon, 24 October: "create a copy in the
            // contractor portal") - the docs NCMB IT has shared with this
            // contractor, once there are some (GET /docs/me can_read), the
            // way Assets waits for its own answer.
            ...(docsForContractors || portalTab === 'docs' ? [['docs', 'Docs']] : []),
            // Ticket templates (088), only for a contractor NCMB has allowed
            // to manage them - see templatesOpen above.
            ...(templatesOpen ? [['templates', 'Templates']] : [])
            // TO DO IS NOT HERE EITHER. It was a sixth tab for a day; Gordon
            // moved it to the top bar between Chat and Availability, for the
            // reason Availability went there (App.js, ContractorTodoPage).
            // AVAILABILITY IS NOT HERE ANY MORE. It sat at the end of this row
            // and it was the one tab that is not about a job in flight: the
            // other five are views of work being held right now, and this is
            // the standing answer to "when can you work". Among them it read
            // as a sixth pile of work.
            // Gordon: "Move availability to the top bar next to chat." It is a
            // navbar item beside Chat now (App.js) and a page of its own at
            // /availability, reachable from anywhere rather than only from
            // here.
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tab-button ${portalTab === key ? 'active' : ''}`}
              /* Assets is the one tab with an address of its own - /assets,
                 the same one staff bookmark - because the register's pages
                 link to each other by it. For a contractor that address
                 renders this portal on this tab (App.js), so the strip stays
                 above the register as Gordon asked. */
              onClick={() => (key === 'assets' ? navigate('/assets')
                : key === 'docs' ? navigate('/docs') : setPortalTab(key))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/*
        * THE BOARD. Gordon: "create a kanban style board with the tickets for
        * the contractor view."
        *
        * Same `assignments` the list view reads - one fetch, one set of work,
        * two ways of looking at it - and the same handleStatusUpdate the
        * list's dropdown calls, so moving a card and picking a status from the
        * list go through exactly one piece of code. A board with its own mover
        * would be a second place for the rule about which statuses a
        * contractor may set to drift out of step with the server's.
        */}
      {/*
        * THE REGISTER, IN THE PORTAL. Gordon, 22 October: "when I am on the
        * contractor's portal and I click on Assets - can you have the sub
        * menu appearing below the top bar above Assets." The Assets tab used
        * to leave for /assets, a page of its own, and the strip went with it.
        * Now /assets and /assets/:id ARE this portal on this tab for a
        * contractor (App.js), and the same two pages staff use are drawn
        * here: the list, or one device when the route names it. They keep
        * their own links - a row opens /assets/:id, "← Assets" goes back to
        * /assets - and both stay under the strip.
        */}
      {portalTab === 'assets' && (
        <div className="cd-assets-pane">
          {assetId ? <AssetDetail key={assetId} /> : <AssetsPage />}
        </div>
      )}

      {/* DOCUMENTATION, IN THE PORTAL, as the register is: /docs and
          /docs/:id are this portal on this tab for a contractor (App.js),
          and the same reader staff use is drawn under the strip. Its links
          are /docs/:id, so reading on keeps the strip above it. */}
      {portalTab === 'docs' && (
        <div className="cd-docs-pane">
          <DocsPage />
        </div>
      )}

      {/* CREATE TICKET, IN THE PORTAL (Gordon, 26 October: "why don't we have
          the standard ticket here?"). /create is this portal on this pane for
          a contractor (App.js, pages/ContractorCreateTicket.js), and the one
          form everybody raises a ticket with is drawn under the strip - it
          shapes itself for a contractor (pages/CreateTicket.js). No tab of
          the strip is lit: raising a request is a thing done from the bar's
          Tickets menu, not a view of the work. */}
      {portalTab === 'create' && (
        <div className="cd-create-pane">
          <CreateTicket />
        </div>
      )}

      {/* REQUEST SOMETHING, IN THE PORTAL (124): /request and
          /request/:itemId are this pane for a contractor - the catalogue's
          items marked for contractors too, under the strip, as Create
          ticket is. No tab is lit. */}
      {portalTab === 'request' && (
        <div className="cd-create-pane">
          <RequestCatalogue />
        </div>
      )}

      {portalTab === 'board' && (
        <>
          {/* The same alert markup the list view uses, because a failed
              move fails the same way whichever view asked for it. */}
          {statusError && (
            <div role="alert" style={{
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              backgroundColor: 'var(--color-bg-alt)',
              borderLeft: '3px solid var(--color-critical)',
              borderRadius: '6px',
              color: 'var(--color-text-primary)',
              fontSize: '0.875rem'
            }}>{statusError}</div>
          )}
          <ContractorKanban
            tickets={assignments}
            onMove={handleStatusUpdate}
            moving={updatingStatus}
            onOpen={(t) => openAssignment(t.id)}
          />
        </>
      )}

      {/* The same list the old /my-work-orders page drew, drawing itself:
          it reads the contractor from the portal token, or from View as. */}
      {portalTab === 'myorders' && <WorkOrdersList isAdmin={false} />}

      {portalTab === 'templates' && templatesOpen && (
        <div className="cd-templates-pane">
          <TicketTemplates portal />
        </div>
      )}

      {portalTab === 'orders' && (
        <ContractorWorkOrders contractorId={contractorId} token={contractorToken} />
      )}

      {/* Their own contract terms (120), read-only, above the queue - drawn
          only when they have a contract to show. */}
      {portalTab === 'billing' && (
        <>
          <MyContract contractorId={contractorId} token={contractorToken} />
          <ContractorBilling contractorId={contractorId} token={contractorToken} />
        </>
      )}

      {/* ISSUED BILLS, two ways round. By ticket is the list it always was;
          by invoice is the Invoicing view the office has, over this
          contractor's invoices only - each opening onto its tickets with the
          budget, the hours worked and billed, and the expenses. */}
      {portalTab === 'billed' && (
        <>
          <div className="cd-billed-switch" role="group" aria-label="Show issued bills">
            {[['ticket', 'By ticket'], ['invoice', 'By invoice']].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={billedBy === k}
                      className={billedBy === k ? 'cd-billed-opt is-on' : 'cd-billed-opt'}
                      onClick={() => setBilledBy(k)}>
                {label}
              </button>
            ))}
          </div>
          {billedBy === 'invoice'
            ? <InvoicingView contractorId={contractorId} token={contractorToken}
                             ticketHref={(id) => `/my-tickets/${id}`} />
            : <ContractorBilled contractorId={contractorId} token={contractorToken} />}
        </>
      )}

      {portalTab === 'work' && (
        <>
      {/*
        * THE NUMBERS WORTH HAVING AT THE TOP OF THE PAGE.
        *
        * Gordon: "in the contractor view - add a few more info boxes (not
        * just - Total Assignments, in progress and completed)."
        *
        * The three that were here answered one question - how much work is
        * there - and left the ones a contractor actually opens this page to
        * ask. Four more, each chosen because the tabs below do NOT answer it
        * at a glance and each one is a different decision:
        *
        *   Needs attention - open or in progress at High or Critical. What to
        *     do first, which is the only question that matters at 8am.
        *   Waiting on others - awaiting approval plus on hold. Work that is
        *     mine and that I cannot move, which is the pile people forget
        *     they are owed an answer on.
        *   Unbilled - finished and not invoiced. The money. lib/ticketStage
        *     is explicit that a Resolved or Approved ticket is always
        *     uninvoiced, because invoicing closes it.
        *   Sent back - an approver rejected the hours and handed the job
        *     back. Nothing else on this page says so above the fold, and it
        *     is the one pile where the contractor is the blocker.
        *
        * Every one is derived from `assignments`, which the page has already
        * fetched - no new request, and nothing that can disagree with the
        * tabs, because the stage rule is the shared one in lib/ticketStage.
        */}
      <div className="contractor-stat-row">
        <ContractorStat
          value={assignments.length}
          label="Total Assignments"
          tone="var(--color-primary)"
          onOpen={() => setDrill({ title: 'All assignments', tickets: assignments })}
        />
        <ContractorStat
          value={needsAttention.length}
          label="Needs attention"
          tone="var(--color-critical)"
          onOpen={() => setDrill({ title: 'Needs attention', tickets: needsAttention })}
        />
        <ContractorStat
          value={byStatus('In Progress').length}
          label="In Progress"
          tone="var(--color-info)"
          onOpen={() => setDrill({ title: 'In progress', tickets: byStatus('In Progress') })}
        />
        <ContractorStat
          value={waitingOnOthers.length}
          label="Waiting on others"
          tone="var(--color-warning)"
          onOpen={() => setDrill({ title: 'Waiting on others', tickets: waitingOnOthers })}
        />
        <ContractorStat
          value={unbilled.length}
          label="Unbilled"
          tone="var(--color-accent)"
          onOpen={() => setDrill({ title: 'Finished, not invoiced', tickets: unbilled })}
        />
        <ContractorStat
          value={byStatus('Resolved').length}
          label="Completed"
          tone="var(--color-success)"
          onOpen={() => setDrill({ title: 'Completed', tickets: byStatus('Resolved') })}
        />
        <ContractorStat
          value={sentBack.length}
          label="Sent back"
          tone="var(--color-critical)"
          onOpen={() => setDrill({ title: 'Sent back for more detail', tickets: sentBack })}
        />
      </div>


      {/* THE STAGES, WITH COUNTS.
          //
          Was six buttons - All, Open, In Progress, On Hold, Resolved, Closed -
          filtering on an exact status match. ticket_statuses holds SEVEN
          names: those five plus "Awaiting Approval" and "Approved", so a
          ticket in either appeared under All and under no button at all. And
          "Approved" is the status a ticket takes the moment its hours are
          signed off, which is exactly the work a contractor is waiting to be
          paid for - the thing Gordon asked for a tab for.
          //
          The stages are a partition now (lib/ticketStage), every tab carries
          its count, and the row says so out loud if the counts ever stop
          adding up to All. "If I have 20 tickets and I go through the
          different classifications, they are all there" is answerable by
          looking at this row. */}
      <TicketStageTabs
        tickets={assignments}
        stage={stage}
        onStage={setStage}
        idPrefix="cd-stage"
      />

      {/* Sort and narrow within the stage. See visibleAssignments above for
          why this sits under the tabs rather than replacing them. */}
      <div className="cd-toolbar" role="group" aria-label="Sort and filter tickets">
        <label className="cd-tool">
          <span>Sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="updated">Recently updated</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
          </select>
        </label>
        <label className="cd-tool">
          <span>Priority</span>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </label>
        <label className="cd-tool cd-tool-grow">
          <span>Find</span>
          <input
            type="search"
            value={query}
            placeholder="Ticket number or title"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Find a ticket by number or title"
          />
        </label>
        {narrowed && (
          <button type="button" className="cd-tool-clear" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      {statusError && (
        <div role="alert" style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          backgroundColor: 'var(--color-bg-alt)',
          borderLeft: '3px solid var(--color-critical)',
          borderRadius: '6px',
          color: 'var(--color-text-primary)',
          fontSize: '0.875rem'
        }}>
          {statusError}
        </div>
      )}

      {/* Assignments List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
          Loading assignments...
        </div>
      ) : loadError ? (
        <div role="alert" style={{
          padding: '1.25rem 1.5rem',
          backgroundColor: 'var(--color-bg-alt)',
          border: '1px solid var(--color-danger)',
          borderLeft: '4px solid var(--color-danger)',
          borderRadius: '8px'
        }}>
          <strong>{loadError}</strong>
          <p style={{ margin: '0.5rem 0 0', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
            Nothing is missing from this contractor&rsquo;s record &mdash; the list
            could not be fetched. Try Refresh; if it keeps failing the backend log
            will say why.
          </p>
        </div>
      ) : visibleAssignments.length === 0 ? (
        <div style={{
          padding: '1.5rem',
          backgroundColor: 'var(--color-bg-alt)',
          borderRadius: '8px',
          color: 'var(--color-text-secondary)'
        }}>
          <strong style={{ color: 'var(--color-text-primary)' }}>
            {assignments.length === 0
              ? 'No tickets are assigned to this contractor'
              : filteredAssignments.length > 0
                ? 'Nothing matches these filters'
                : 'Nothing at this stage'}
          </strong>
          {/* Three different facts: no work at all, work at this stage that
              the filters hide, and no work at this stage. Only the second
              can be fixed from here, so only it gets a button. */}
          {assignments.length > 0 && filteredAssignments.length > 0 ? (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
              {filteredAssignments.length} ticket{filteredAssignments.length === 1 ? '' : 's'} at
              this stage. <button type="button" className="cd-tool-clear"
                onClick={clearFilters}>Clear the filters</button> to see them.
            </p>
          ) : assignments.length === 0 ? (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
              This portal lists tickets whose <em>Contractor</em> field names this
              contractor. A ticket assigned to a member of staff sits in the
              <em> Assignee</em> field instead and will not appear here until a
              contractor is set on it too &mdash; either on the ticket itself, or by
              assigning one when the work order is approved.
            </p>
          ) : (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
              {assignments.length} ticket{assignments.length === 1 ? '' : 's'} assigned
              in total. Choose <em>All</em> to see them.
            </p>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {visibleAssignments.map(assignment => (
            /* COLLAPSED TO THE FACTS, and shaded by stage.
               //
               This card held the description, a status select, a timesheet
               with its own form, the expenses panel with its own form, the
               files, the custom fields and a comment thread - four forms in a
               350px grid cell, half of them behind a "Show Details" button.
               That is why it kept being folded, and why Gordon could not find
               the expenses panel on it. It now shows what identifies a job,
               down to priority and created as he asked, and pressing it opens
               the work in a dialog where there is room to do it.
               //
               data-stage drives the shading. The colours live in
               TicketStageTabs.css beside the tabs that filter on them, so a
               stage looks the same wherever it appears - which is the whole
               point of shading it at all. */
            <button
              type="button"
              key={assignment.id}
              /* A stable target, so pressing a ticket in the list a stat tile
                 opens can bring the reader to that card. */
              id={`assignment-${assignment.id}`}
              className="ac-card stg-shade"
              data-stage={stageOf(assignment)}
              onClick={() => openAssignment(assignment.id)}
              aria-label={`Open ${assignment.ticket_number}: ${assignment.title || ''}`}
            >
              <span className="ac-top">
                <span className="ac-number">{assignment.ticket_number}</span>
                {/* The status in WORDS as well as in colour. Nobody should have
                    to learn a palette to read a list, and a tint alone is no
                    use to a reader who cannot separate them. */}
                <span className="ac-status" style={{
                  backgroundColor: getStatusColor(assignment.status) + '20',
                  color: getStatusColor(assignment.status),
                  borderColor: getStatusColor(assignment.status) + '66'
                }}>
                  <i className="ac-status-dot" style={{ backgroundColor: getStatusColor(assignment.status) }} aria-hidden="true" />
                  {assignment.status}
                  {/* The stage, when it says something the status does not.
                      "Resolved" and "Approved" are both money still owed;
                      "Unbilled" is the word Gordon looks for. */}
                  {stageOf(assignment) === 'unbilled' && assignment.status !== 'Closed' && (
                    <span className="ac-status-stage">· Unbilled</span>
                  )}
                </span>
              </span>

              <span className="ac-title">{assignment.title}</span>

              {/* SENT BACK STAYS ON THE FACE OF THE CARD, REASON AND ALL.
                  //
                  Collapsing the card to "priority and created" is right for
                  everything else, but not for this: the approver's words are
                  the one thing that tells the contractor what to put right,
                  and this notice exists because a refused ticket used to
                  reappear as In Progress with nothing anywhere saying it had
                  come back. Reducing it to a badge and hiding the reason
                  behind a click would have reintroduced most of that bug -
                  e2e/notifications.mjs caught me doing exactly that. */}
              {assignment.sent_back_reason && (
                <span className="ac-sentback">
                  <span className="ac-sentback-head">
                    Sent back{assignment.sent_back_by ? ` by ${assignment.sent_back_by}` : ''}
                    {assignment.sent_back_at
                      ? ` · ${String(assignment.sent_back_at).slice(0, 10)}` : ''}
                  </span>
                  <span className="ac-sentback-why">{assignment.sent_back_reason}</span>
                  {assignment.sent_back_hours != null && (
                    <span className="ac-sentback-hours">
                      You had submitted {Number(assignment.sent_back_hours)}h.
                      Your time is editable again.
                    </span>
                  )}
                </span>
              )}

              <span className="ac-meta">
                <span className="ac-field">
                  <span className="ac-field-label">Priority</span>
                  <span className="ac-chip" style={{
                    backgroundColor: getPriorityColor(assignment.priority) + '20',
                    color: getPriorityColor(assignment.priority)
                  }}>
                    {assignment.priority}
                  </span>
                </span>
                <span className="ac-field">
                  <span className="ac-field-label">Created</span>
                  <span className="ac-field-value">
                    {fmtDate(assignment.created_at)}
                  </span>
                </span>
                {/* What Unbilled means on the row it applies to, rather than
                    only on the tab. */}
                {assignment.invoice_number && (
                  <span className="ac-field">
                    <span className="ac-field-label">Invoice</span>
                    <span className="ac-field-value">{assignment.invoice_number}</span>
                  </span>
                )}
              </span>

              <span className="ac-open">Open &rsaquo;</span>
            </button>
          ))}
        </div>
      )}

      {/*
        * RAISED BY ME. Gordon, 22 October: "Contractors raising tickets - yes
        * allow them to raise tickets as well - they might need subscriptions
        * or to buy things for the company." These are those: requests to the
        * firm, waiting on an approver or decided, listed apart from the jobs
        * above because they are something asked for rather than something to
        * do. Each opens on the contractor's own ticket page, where the state
        * of the request is explained. Always drawn on this tab, even empty,
        * because the way to raise one lives here.
        */}
      {!loading && !loadError && (
        <section className="cd-raised" aria-labelledby="cd-raised-title">
          <div className="cd-raised-head">
            <h2 id="cd-raised-title" className="cd-raised-title">
              Raised by me
              <span className="cd-raised-count">{raised.length}</span>
            </h2>
            <Link className="btn btn-secondary cd-raised-new" to="/create">+ New request</Link>
          </div>
          {raised.length === 0 ? (
            <p className="cd-raised-empty">
              Need something for the work &mdash; a subscription, a part, access to a
              system? Raise a request and it goes to an approver.
            </p>
          ) : (
            <div className="cd-raised-grid">
              {raised.map((t) => {
                const word = requestWord(t);
                return (
                  <button type="button" key={t.id} className="ac-card cd-request"
                          data-decision={word.key}
                          onClick={() => openAssignment(t.id)}
                          aria-label={`Open ${t.ticket_number}: ${t.title || ''}`}>
                    <span className="ac-top">
                      <span className="ac-number">{t.ticket_number}</span>
                      <span className="cd-request-state">{word.label}</span>
                    </span>
                    <span className="ac-title">{t.title}</span>
                    <span className="ac-meta">
                      <span className="ac-field">
                        <span className="ac-field-label">Priority</span>
                        <span className="ac-chip" style={{
                          backgroundColor: getPriorityColor(t.priority) + '20',
                          color: getPriorityColor(t.priority)
                        }}>{t.priority}</span>
                      </span>
                      <span className="ac-field">
                        <span className="ac-field-label">Raised</span>
                        <span className="ac-field-value">{fmtDate(t.created_at)}</span>
                      </span>
                      {t.category && (
                        <span className="ac-field">
                          <span className="ac-field-label">Category</span>
                          <span className="ac-field-value">{t.category}</span>
                        </span>
                      )}
                    </span>
                    <span className="ac-open">Open &rsaquo;</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}
        </>
      )}

      {drill && (
        <TicketListModal
          title={drill.title}
          tickets={drill.tickets}
          onClose={() => setDrill(null)}
          /* Without this the modal navigated to /tickets/:id, which is a
             staff route - so a contractor pressing one of their own tickets
             was redirected to /contractor-login and thrown out of the portal.
             Here, opening a ticket means opening it on this page. */
          onOpen={(t) => openAssignment(t.id)}
        />
      )}
    </div>
  );
}

export default ContractorDashboard;
